import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, parseJsonSafe, handleUnauthorized } from '../../utils/api';
import { downloadFile, previewFile } from '../../utils/invoiceUtils';

const OPEN_STATES = ['Borrador', 'Enviado', 'Pendiente'];

// Estado efectivo (deriva "Caducado" cuando un presupuesto abierto supera su validez)
const effectiveStatus = (b) => {
  if (OPEN_STATES.includes(b.estado) && b.valid_until) {
    const d = new Date(b.valid_until);
    if (!Number.isNaN(d.getTime()) && d < new Date(new Date().toDateString())) return 'Caducado';
  }
  return b.estado;
};

const emptyEmail = { open: false, sending: false, error: '', to: '', subject: '', templateId: '', bodyHtml: '', budgetNumber: '' };
const emptyWhatsapp = { open: false, loading: false, sending: false, error: '', phone: '', clientName: '', budgetNumber: '' };

/**
 * Menú de acciones de un presupuesto (Editar/Ver + dropdown "Acciones").
 * Autocontenido: incluye sus propios modales de email/WhatsApp y carga las
 * plantillas de email bajo demanda. Se usa tanto en /presupuestos como en la
 * pestaña Presupuestos de la ficha de cliente para tener las mismas acciones.
 *
 * Props:
 *  - budget: fila del presupuesto (id, presupuesto, estado, valid_until, converted, client_id, razon_social, dolibarr_proposal_id)
 *  - token: JWT del staff
 *  - onRefresh: callback para recargar el listado tras un cambio (estado/envío/borrado)
 */
export default function BudgetActionsMenu({ budget, token, onRefresh }) {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [acting, setActing] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [emailModal, setEmailModal] = useState(emptyEmail);
  const [whatsappModal, setWhatsappModal] = useState(emptyWhatsapp);

  const est = effectiveStatus(budget);
  const isDolibarr = Boolean(budget.dolibarr_proposal_id);
  const authHeaders = { Authorization: `Bearer ${token}` };
  const refresh = () => { if (typeof onRefresh === 'function') onRefresh(); };

  // Cerrar el menú al hacer clic fuera
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const download = async (endpoint, filename) => {
    setOpen(false);
    try { await downloadFile(endpoint, filename, token, handleUnauthorized); }
    catch (err) { alert(err.message); }
  };

  const preview = async (endpoint) => {
    setOpen(false);
    try { await previewFile(endpoint, token, handleUnauthorized); }
    catch (err) { alert(err.message); }
  };

  const changeStatus = async (estado) => {
    if (acting) return;
    setOpen(false);
    setActing(true);
    try {
      const res = await apiFetch(`/api/clients/budgets/${budget.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ estado })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((data && data.message) || 'No se pudo cambiar el estado');
      refresh();
    } catch (err) { alert(err.message); } finally { setActing(false); }
  };

  const duplicate = async () => {
    if (acting) return;
    setOpen(false);
    setActing(true);
    try {
      const res = await apiFetch(`/api/clients/budgets/${budget.id}/duplicate`, { method: 'POST', headers: authHeaders });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((data && data.message) || 'No se pudo duplicar');
      navigate(`/presupuestos/${data.id}`);
    } catch (err) { alert(err.message); } finally { setActing(false); }
  };

  const convertToInvoice = async () => {
    if (acting) return;
    setOpen(false);
    if (!window.confirm('¿Convertir este presupuesto en factura (borrador)?')) return;
    setActing(true);
    try {
      const res = await apiFetch(`/api/clients/budgets/${budget.id}/convert-to-invoice`, { method: 'POST', headers: authHeaders });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((data && data.message) || 'No se pudo convertir');
      alert(`Factura creada: ${data.numero || ''}`);
      navigate(`/facturas/${data.id}`);
    } catch (err) { alert(err.message); } finally { setActing(false); }
  };

  const remove = async () => {
    if (acting) return;
    setOpen(false);
    if (!window.confirm('¿Eliminar este presupuesto?')) return;
    setActing(true);
    try {
      const res = await apiFetch(`/api/clients/budgets/${budget.id}`, { method: 'DELETE', headers: authHeaders });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((data && data.message) || 'Error eliminando presupuesto');
      refresh();
    } catch (err) { alert(err.message); } finally { setActing(false); }
  };

  // --- Email ---
  const openEmail = async () => {
    setOpen(false);
    const baseNumber = budget.presupuesto || `#${budget.id}`;
    let tpls = templates;
    if (!tpls.length) {
      try {
        const res = await apiFetch('/api/email/templates', { headers: authHeaders });
        const data = await parseJsonSafe(res);
        if (res.ok && Array.isArray(data)) { tpls = data; setTemplates(data); }
      } catch (_) { /* sin plantillas */ }
    }
    const defaultTemplateId = tpls[0]?.id ? String(tpls[0].id) : '';
    const template = tpls.find((t) => String(t.id) === defaultTemplateId);
    setEmailModal({
      open: true, sending: false, error: '', to: '',
      subject: `Presupuesto ${baseNumber}`, templateId: defaultTemplateId,
      bodyHtml: template?.body_html || '', budgetNumber: baseNumber
    });
    if (budget.client_id) {
      try {
        const res = await apiFetch(`/api/clients/${budget.client_id}`, { headers: authHeaders });
        const data = await parseJsonSafe(res);
        if (res.ok) setEmailModal((prev) => ({ ...prev, to: data.email || prev.to }));
      } catch (_) { /* noop */ }
    }
  };

  const sendEmail = async () => {
    if (!emailModal.to.trim()) return setEmailModal((p) => ({ ...p, error: 'El email es obligatorio.' }));
    setEmailModal((p) => ({ ...p, sending: true, error: '' }));
    try {
      const res = await apiFetch(`/api/clients/budgets/${budget.id}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ to: emailModal.to, subject: emailModal.subject, html: emailModal.bodyHtml, templateId: emailModal.templateId || undefined })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((data && data.message) || 'No se pudo enviar por email');
      setEmailModal(emptyEmail);
      refresh();
    } catch (err) { setEmailModal((p) => ({ ...p, sending: false, error: err.message })); }
  };

  // --- WhatsApp ---
  const openWhatsapp = async () => {
    setOpen(false);
    setWhatsappModal({ ...emptyWhatsapp, open: true, loading: true, clientName: budget.razon_social || '', budgetNumber: budget.presupuesto || `#${budget.id}` });
    if (!budget.client_id) {
      setWhatsappModal((p) => ({ ...p, loading: false, error: 'Presupuesto sin cliente asociado.' }));
      return;
    }
    try {
      const res = await apiFetch(`/api/clients/${budget.client_id}`, { headers: authHeaders });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((data && data.message) || 'Error cargando cliente');
      setWhatsappModal((p) => ({ ...p, loading: false, phone: data.telefono || '', clientName: data.razon_social || p.clientName }));
    } catch (err) { setWhatsappModal((p) => ({ ...p, loading: false, error: err.message })); }
  };

  const sendWhatsapp = async () => {
    if (!whatsappModal.phone.trim()) return setWhatsappModal((p) => ({ ...p, error: 'El teléfono es obligatorio.' }));
    setWhatsappModal((p) => ({ ...p, sending: true, error: '' }));
    try {
      const res = await apiFetch(`/api/clients/budgets/${budget.id}/send-whatsapp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({})
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((data && data.message) || 'No se pudo enviar por WhatsApp');
      setWhatsappModal(emptyWhatsapp);
      refresh();
    } catch (err) { setWhatsappModal((p) => ({ ...p, sending: false, error: err.message })); }
  };

  return (
    <>
      <div ref={rootRef} className="btn-group btn-group-sm budget-actions-menu" role="group" style={{ position: 'relative' }}>
        <button className="btn btn-outline-secondary" type="button" onClick={() => navigate(`/presupuestos/${budget.id}`)}>
          {isDolibarr ? 'Ver' : 'Editar'}
        </button>
        <button className="btn btn-outline-secondary dropdown-toggle" type="button" aria-expanded={open} disabled={acting} onClick={() => setOpen((o) => !o)}>
          {acting ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : 'Acciones'}
        </button>
        <ul className={`dropdown-menu dropdown-menu-end${open ? ' show' : ''}`} style={{ position: 'absolute', right: 0, top: '100%' }}>
          <li><button className="dropdown-item" type="button" onClick={() => preview(`/api/clients/budgets/${budget.id}/pdf`)}>Previsualizar PDF</button></li>
          <li><button className="dropdown-item" type="button" onClick={() => download(`/api/clients/budgets/${budget.id}/pdf`, `${budget.presupuesto || 'presupuesto'}.pdf`)}>Descargar PDF</button></li>
          <li><button className="dropdown-item" type="button" onClick={() => download(`/api/clients/budgets/${budget.id}/pptx`, `${budget.presupuesto || 'presupuesto'}.pptx`)}>Descargar PPTX</button></li>
          {isDolibarr && <li><button className="dropdown-item" type="button" onClick={() => preview(`/api/clients/budgets/${budget.id}/dolibarr-pdf`)}>Previsualizar PDF Dolibarr</button></li>}
          {isDolibarr && <li><button className="dropdown-item" type="button" onClick={() => download(`/api/clients/budgets/${budget.id}/dolibarr-pdf`, `${budget.presupuesto || 'presupuesto'}.pdf`)}>Descargar PDF Dolibarr</button></li>}
          <li><button className="dropdown-item" type="button" onClick={openEmail}>Enviar por email</button></li>
          <li><button className="dropdown-item" type="button" onClick={openWhatsapp}>Enviar por WhatsApp</button></li>
          <li><hr className="dropdown-divider" /></li>
          {!isDolibarr && est !== 'Aceptado' && <li><button className="dropdown-item text-success" type="button" onClick={() => changeStatus('Aceptado')}>Marcar aceptado</button></li>}
          {!isDolibarr && est !== 'Rechazado' && <li><button className="dropdown-item text-danger" type="button" onClick={() => changeStatus('Rechazado')}>Marcar rechazado</button></li>}
          {!isDolibarr && est === 'Borrador' && <li><button className="dropdown-item" type="button" onClick={() => changeStatus('Enviado')}>Marcar enviado</button></li>}
          <li><button className="dropdown-item" type="button" onClick={duplicate}>Duplicar</button></li>
          {!budget.converted && <li><button className="dropdown-item" type="button" onClick={convertToInvoice}>Convertir a factura</button></li>}
          {!isDolibarr && (
            <>
              <li><hr className="dropdown-divider" /></li>
              <li><button className="dropdown-item text-danger" type="button" onClick={remove}>Eliminar</button></li>
            </>
          )}
        </ul>
      </div>

      {emailModal.open && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Enviar presupuesto {emailModal.budgetNumber} por email</h5>
                <button className="btn-close" onClick={() => setEmailModal((p) => ({ ...p, open: false }))}></button>
              </div>
              <div className="modal-body">
                {emailModal.error && <div className="alert alert-danger py-2">{emailModal.error}</div>}
                <div className="mb-2">
                  <label className="form-label small">Para</label>
                  <input className="form-control" value={emailModal.to} onChange={(e) => setEmailModal((p) => ({ ...p, to: e.target.value }))} placeholder="cliente@email.com" />
                </div>
                <div className="mb-2">
                  <label className="form-label small">Asunto</label>
                  <input className="form-control" value={emailModal.subject} onChange={(e) => setEmailModal((p) => ({ ...p, subject: e.target.value }))} />
                </div>
                {templates.length > 0 && (
                  <div className="mb-2">
                    <label className="form-label small">Plantilla</label>
                    <select className="form-select" value={emailModal.templateId} onChange={(e) => { const t = templates.find((x) => String(x.id) === e.target.value); setEmailModal((p) => ({ ...p, templateId: e.target.value, bodyHtml: t?.body_html || p.bodyHtml })); }}>
                      <option value="">(Sin plantilla)</option>
                      {templates.map((t) => <option key={t.id} value={String(t.id)}>{t.name || `Plantilla #${t.id}`}</option>)}
                    </select>
                  </div>
                )}
                <div className="text-muted small">Se adjuntará el PDF del presupuesto (el archivo marcado como &quot;PDF presupuesto&quot; si lo hay).</div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setEmailModal((p) => ({ ...p, open: false }))}>Cancelar</button>
                <button className="btn btn-primary" onClick={sendEmail} disabled={emailModal.sending}>{emailModal.sending ? 'Enviando...' : 'Enviar'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {whatsappModal.open && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Enviar presupuesto {whatsappModal.budgetNumber} por WhatsApp</h5>
                <button className="btn-close" onClick={() => setWhatsappModal((p) => ({ ...p, open: false }))}></button>
              </div>
              <div className="modal-body">
                {whatsappModal.error && <div className="alert alert-danger py-2">{whatsappModal.error}</div>}
                {whatsappModal.loading ? <p>Cargando datos del cliente...</p> : (
                  <>
                    <div className="mb-2"><strong>{whatsappModal.clientName}</strong></div>
                    <div className="mb-2">
                      <label className="form-label small">Teléfono</label>
                      <input className="form-control" value={whatsappModal.phone} onChange={(e) => setWhatsappModal((p) => ({ ...p, phone: e.target.value }))} placeholder="+34..." />
                    </div>
                    <div className="text-muted small">Se enviará el PDF del presupuesto mediante la plantilla de WhatsApp configurada.</div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setWhatsappModal((p) => ({ ...p, open: false }))}>Cancelar</button>
                <button className="btn btn-primary" onClick={sendWhatsapp} disabled={whatsappModal.sending || whatsappModal.loading}>{whatsappModal.sending ? 'Enviando...' : 'Enviar'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
